/**
 * Draft markdown → safe HTML. Skills write headings, lists, tables and
 * `[citation]` tags (`packages/skills/src/cited-draft.ts`). No dependency:
 * escape first, then add only the tags this file emits.
 */

export function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function inline(escaped: string): string {
  return escaped
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(((?:https?:|\/)[^)\s]+)\)/g, '<a href="$2" rel="noreferrer">$1</a>')
    .replace(/\[([^\]]+)\]/g, '<span class="cite">$1</span>');
}

function isSep(line: string): boolean {
  return /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
}

function cells(line: string): string[] {
  return line.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}

export function renderMarkdown(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let i = 0;
  const para: string[] = [];
  const flush = () => {
    if (!para.length) return;
    out.push(`<p>${inline(escapeHtml(para.join(" ")))}</p>`);
    para.length = 0;
  };
  while (i < lines.length) {
    const raw = lines[i] ?? "";
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); i++; continue; }
    const heading = /^(#{1,3})\s+(.+)$/.exec(line.trim());
    if (heading) {
      flush();
      const n = heading[1]!.length;
      out.push(`<h${n}>${inline(escapeHtml(heading[2]!.trim()))}</h${n}>`);
      i++;
      continue;
    }
    if (/^[-*_]{3,}$/.test(line.trim())) { flush(); out.push("<hr>"); i++; continue; }
    const next = lines[i + 1] ?? "";
    if (line.includes("|") && isSep(next)) {
      flush();
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").includes("|") && !isSep(lines[i] ?? "")) {
        rows.push(cells(lines[i] ?? ""));
        i++;
      }
      const th = head.map((c) => `<th>${inline(escapeHtml(c))}</th>`).join("");
      const body = rows.map((r) => `<tr>${r.map((c) => `<td>${inline(escapeHtml(c))}</td>`).join("")}</tr>`).join("");
      out.push(`<div class="md-table"><table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table></div>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      flush();
      out.push("<ul>");
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i] ?? "")) {
        out.push(`<li>${inline(escapeHtml((lines[i] ?? "").replace(/^\s*[-*]\s+/, "")))}</li>`);
        i++;
      }
      out.push("</ul>");
      continue;
    }
    para.push(line.trim());
    i++;
  }
  flush();
  return out.join("");
}
